import { MetaCell, NoiumWordmark } from "./shared";
import { getProposalTexts, type ProposalLanguage } from "./i18n";

export type HeroClientContact = {
  name: string | null;
  jobTitle: string | null;
  email: string | null;
};

export type HeroProps = {
  quoteNumber: string | null;
  validUntil: string | null;
  preparedAt: string | null;
  clientCompany: string | null;
  clientPerson: string | null;
  clientContacts?: HeroClientContact[];
  preparerName: string;
  preparerEmail: string | null;
  language: ProposalLanguage;
};

export function ProposalHero({
  quoteNumber,
  validUntil,
  preparedAt,
  clientCompany,
  clientPerson,
  clientContacts,
  preparerName,
  preparerEmail,
  language,
}: HeroProps) {
  const t = getProposalTexts(language).hero;
  // One line per company contact: "Name · Job title", falling back to whatever
  // identifier exists. When there are no linked contacts we keep showing the
  // single opportunity contact (clientPerson) as before.
  const contactLines = (clientContacts ?? [])
    .map((c) => {
      const name = c.name?.trim();
      const title = c.jobTitle?.trim();
      if (name && title) return `${name} · ${title}`;
      return name || title || c.email?.trim() || null;
    })
    .filter((line): line is string => !!line);
  return (
    <section
      className="relative overflow-hidden text-white"
      style={{ background: "#0E0E12" }}
    >
      {/* Radial gradient art element */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 40%, rgba(30,88,255,0.55) 0%, rgba(0,252,203,0.18) 38%, transparent 70%)",
          filter: "blur(2px)",
        }}
      />

      <div className="relative mx-auto max-w-5xl px-5 sm:px-10">
        {/* top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-7 sm:pt-12">
          <NoiumWordmark dark />
          {quoteNumber && (
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white sm:text-[11px]">
              {quoteNumber}
            </div>
          )}
        </div>

        {/* eyebrow + headline */}
        <div className="pt-14 pb-10 sm:pt-32 sm:pb-20">
          <div className="mb-6 flex items-center gap-3 sm:mb-7">
            <div
              className="h-[2px] w-9 rounded-full"
              style={{
                background: "linear-gradient(90deg, #1E58FF 0%, #00FCCB 100%)",
              }}
            />
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400 sm:text-[11px]">
              {t.eyebrow}
            </div>
          </div>
          <h1 className="font-display text-[34px] font-extrabold leading-[1.02] tracking-tightest sm:text-6xl sm:leading-[0.96] md:text-[80px]">
            {t.headlineLine1}
            <br />
            <span className="brand-gradient-text">{t.headlineLine2}</span>
          </h1>
          <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-zinc-400 sm:mt-7 sm:text-lg">
            {clientCompany
              ? t.descriptionWithCompany(clientCompany)
              : t.descriptionNoCompany}
          </p>
        </div>

        {/* meta row */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 border-t border-white/10 pb-10 pt-6 sm:grid-cols-4 sm:gap-6 sm:gap-x-8 sm:pb-16 sm:pt-9">
          {contactLines.length > 0 ? (
            <div className="min-w-0">
              <div className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                {t.metaClient}
              </div>
              {clientCompany && (
                <div className="mt-2 break-words text-sm font-semibold text-white">
                  {clientCompany}
                </div>
              )}
              <div className={clientCompany ? "mt-1 space-y-0.5" : "mt-2 space-y-0.5"}>
                {contactLines.map((line, i) => (
                  <div key={i} className="break-words text-[11.5px] text-zinc-400">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <MetaCell label={t.metaClient} primary={clientCompany} secondary={clientPerson} />
          )}
          <MetaCell
            label={t.metaPreparedBy}
            primary={preparerName}
            secondary={preparerEmail}
          />
          <MetaCell label={t.metaPreparedAt} primary={preparedAt} secondary={null} />
          <MetaCell
            label={t.metaValidUntil}
            primary={validUntil}
            secondary={quoteNumber ? t.metaProposalSuffix(quoteNumber) : null}
          />
        </div>
      </div>
    </section>
  );
}
